import { Controller } from "@hotwired/stimulus"

/**
 * Stimulus controller for bottle forms with AI extraction capability.
 *
 * On new bottle page:
 * - Shows AI vs manual entry choice
 * - Creates bottle with images immediately, redirects to edit page
 *
 * On edit bottle page:
 * - Shows AI extraction status if pending (form hidden)
 * - Updates form fields when extraction completes via Turbo Streams
 * - Shows form after 30 second timeout if AI doesn't respond
 */
export default class extends Controller {
  static targets = [
    "photoSection",
    "aiChoice",
    "aiSpinner",
    "aiStatus",
    "spinnerText",
    "formFields",
    "frontLabelInput",
    "backLabelInput"
  ]

  static values = {
    clubId: String,
    eventId: String,
    bottleId: String,
    bottleType: String,
    aiExtractionPending: Boolean,
    timeoutSeconds: { type: Number, default: 30 },
    spinnerMessage1: String,
    spinnerMessage2: String,
    spinnerMessage3: String,
    spinnerMessage4: String,
    spinnerMessage5: String,
    spinnerMessage6: String,
    spinnerMessage7: String,
    spinnerMessage8: String,
    spinnerMessage9: String,
    spinnerMessage10: String,
    spinnerMessage11: String,
    spinnerMessage12: String,
    successMessage: String,
    errorPrefix: String,
    errorNoImages: String,
    errorNetwork: String,
    timeoutMessage: String,
    timeoutHint: String
  }

  connect() {
    this.spinnerMessages = [
      this.spinnerMessage1Value,
      this.spinnerMessage2Value,
      this.spinnerMessage3Value,
      this.spinnerMessage4Value,
      this.spinnerMessage5Value,
      this.spinnerMessage6Value,
      this.spinnerMessage7Value,
      this.spinnerMessage8Value,
      this.spinnerMessage9Value,
      this.spinnerMessage10Value,
      this.spinnerMessage11Value,
      this.spinnerMessage12Value
    ]

    this.messageIndex = 0
    this.messageInterval = null
    this.extractionTimeout = null

    // If on edit page with pending AI extraction, start message rotation and timeout
    // (Turbo Stream subscription is handled via turbo_stream_from in the view)
    if (this.aiExtractionPendingValue && this.bottleIdValue) {
      this.startMessageRotation()
      this.startExtractionTimeout()
    }

    // Listen for AI extraction events
    this.boundHandleComplete = this.handleExtractionComplete.bind(this)
    this.boundHandleError = this.handleExtractionError.bind(this)
    window.addEventListener("ai-extraction-complete", this.boundHandleComplete)
    window.addEventListener("ai-extraction-error", this.boundHandleError)
  }

  disconnect() {
    this.stopMessageRotation()
    this.clearExtractionTimeout()

    // Clean up event listeners
    window.removeEventListener("ai-extraction-complete", this.boundHandleComplete)
    window.removeEventListener("ai-extraction-error", this.boundHandleError)
  }

  // Start timeout that shows form if AI doesn't respond in time
  startExtractionTimeout() {
    const timeoutMs = this.timeoutSecondsValue * 1000

    this.extractionTimeout = setTimeout(() => {
      console.log("AI extraction timeout - showing form for manual entry")
      this.handleExtractionTimeout()
    }, timeoutMs)
  }

  clearExtractionTimeout() {
    if (this.extractionTimeout) {
      clearTimeout(this.extractionTimeout)
      this.extractionTimeout = null
    }
  }

  // Called when timeout fires without AI response
  handleExtractionTimeout() {
    this.stopMessageRotation()

    // Update the status area with timeout message
    if (this.hasAiStatusTarget) {
      this.aiStatusTarget.innerHTML = `
        <div class="bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
          <div class="flex items-center gap-3">
            <svg class="w-6 h-6 text-yellow-600 dark:text-yellow-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
            </svg>
            <div>
              <p class="font-medium text-yellow-800 dark:text-yellow-200">
                ${this.timeoutMessageValue || "AI is taking longer than expected"}
              </p>
              <p class="text-sm text-yellow-600 dark:text-yellow-400">
                ${this.timeoutHintValue}
              </p>
            </div>
          </div>
        </div>
      `
    }

    // Show the form for manual entry
    this.showFormAfterExtraction()
  }

  // User chooses manual entry
  chooseManual(event) {
    event.preventDefault()
    this.aiChoiceTarget.classList.add("hidden")
    this.formFieldsTarget.classList.remove("hidden")
  }

  // User chooses AI extraction
  // Creates the bottle immediately with images, then redirects to edit page
  // where AI extraction continues in the background
  async chooseAi(event) {
    event.preventDefault()

    // Check if we have images to process
    const hasFrontLabel = this.frontLabelInputTarget.files.length > 0
    const hasBackLabel = this.backLabelInputTarget.files.length > 0

    if (!hasFrontLabel && !hasBackLabel) {
      this.showError(this.errorNoImagesValue)
      return
    }

    // Hide choice, show spinner
    this.aiChoiceTarget.classList.add("hidden")
    this.aiSpinnerTarget.classList.remove("hidden")

    // Start rotating messages
    this.startMessageRotation()

    try {
      // Create bottle with images and start background AI extraction
      const result = await this.createWithAi()

      if (result.success && result.redirect_url) {
        // Redirect to edit page where AI extraction will complete via Turbo Streams
        window.location.href = result.redirect_url
      } else {
        // Show errors and fall back to manual entry
        const errorMessage = result.errors ? result.errors.join(", ") : "Failed to create bottle"
        this.showError(errorMessage)
        this.aiSpinnerTarget.classList.add("hidden")
        this.formFieldsTarget.classList.remove("hidden")
        this.stopMessageRotation()
      }
    } catch (error) {
      console.error("Create with AI error:", error)
      this.showError(this.errorNetworkValue)
      this.aiSpinnerTarget.classList.add("hidden")
      this.formFieldsTarget.classList.remove("hidden")
      this.stopMessageRotation()
    }
  }

  // Creates a bottle immediately with images attached, starts AI extraction in background
  async createWithAi() {
    const formData = new FormData()

    // Add images
    if (this.frontLabelInputTarget.files.length > 0) {
      formData.append("front_label", this.frontLabelInputTarget.files[0])
    }
    if (this.backLabelInputTarget.files.length > 0) {
      formData.append("back_label", this.backLabelInputTarget.files[0])
    }

    // Add bottle type
    formData.append("bottle_type", this.bottleTypeValue)

    const url = `/clubs/${this.clubIdValue}/events/${this.eventIdValue}/bottles/create_with_ai`

    const response = await fetch(url, {
      method: "POST",
      headers: {
        "X-CSRF-Token": document.querySelector("[name='csrf-token']").content
      },
      body: formData
    })

    return await response.json()
  }

  handleExtractionComplete(event) {
    this.stopMessageRotation()
    this.clearExtractionTimeout()

    // Populate form with extracted data
    this.populateFormFields(event.detail)

    // Show the form with populated data
    this.showFormAfterExtraction()
  }

  handleExtractionError(event) {
    this.stopMessageRotation()
    this.clearExtractionTimeout()

    // Show error in status area
    if (this.hasAiStatusTarget) {
      this.aiStatusTarget.innerHTML = `
        <div class="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <div class="flex items-center gap-3">
            <svg class="w-6 h-6 text-red-600 dark:text-red-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
            </svg>
            <div>
              <p class="font-medium text-red-800 dark:text-red-200">
                ${this.errorPrefixValue} ${event.detail?.error || "AI extraction failed"}
              </p>
              <p class="text-sm text-red-600 dark:text-red-400">
                Please fill in the bottle details manually.
              </p>
            </div>
          </div>
        </div>
      `
    }

    // Show the form for manual entry
    this.showFormAfterExtraction()
  }

  // Shows the form sections after AI extraction completes/fails/times out
  showFormAfterExtraction() {
    // Show the normal photo section (with file inputs)
    if (this.hasPhotoSectionTarget) {
      this.photoSectionTarget.classList.remove("hidden")
    }

    // Show the form fields section
    if (this.hasFormFieldsTarget) {
      this.formFieldsTarget.classList.remove("hidden")
    }

    // Hide the spinner if present
    if (this.hasAiSpinnerTarget) {
      this.aiSpinnerTarget.classList.add("hidden")
    }
  }

  populateFormFields(data) {
    // Common fields
    this.setFieldValue("bottle[name]", data.name)
    this.setFieldValue("bottle[producer]", data.producer)
    this.setFieldValue("bottle[country]", data.country)
    this.setFieldValue("bottle[region]", data.region)
    this.setFieldValue("bottle[year]", data.year)
    this.setFieldValue("bottle[alcohol_percentage]", data.alcohol_percentage)
    this.setFieldValue("bottle[volume_ml]", data.volume_ml)

    // Type-specific fields
    switch (this.bottleTypeValue) {
      case "Wine":
        this.setFieldValue("bottle[wine_type]", data.wine_type)
        this.setFieldValue("bottle[vineyard]", data.vineyard)
        this.setFieldValue("bottle[appellation]", data.appellation)
        this.setFieldValue("bottle[age]", data.age)
        // Grapes - trigger multiselect if present
        if (data.grapes && data.grapes.length > 0) {
          this.setMultiselectValues("bottle[grapes][]", data.grapes)
        }
        break
      case "Beer":
        this.setFieldValue("bottle[beer_style]", data.beer_style)
        this.setFieldValue("bottle[ibu]", data.ibu)
        // Hops - trigger multiselect if present
        if (data.hops && data.hops.length > 0) {
          this.setMultiselectValues("bottle[hops][]", data.hops)
        }
        break
      case "Whisky":
        this.setFieldValue("bottle[spirit_type]", data.spirit_type)
        this.setFieldValue("bottle[age]", data.age)
        this.setFieldValue("bottle[cask_type]", data.cask_type)
        break
      case "Rum":
        this.setFieldValue("bottle[spirit_type]", data.spirit_type)
        this.setFieldValue("bottle[age]", data.age)
        this.setFieldValue("bottle[cask_type]", data.cask_type)
        break
      case "Gin":
        this.setFieldValue("bottle[spirit_type]", data.spirit_type)
        this.setFieldValue("bottle[botanical_notes]", data.botanical_notes)
        break
    }
  }

  setFieldValue(name, value) {
    if (value === null || value === undefined) return

    const field = this.element.querySelector(`[name="${name}"]`)
    if (field) {
      field.value = value
      // Trigger change event for any listeners
      field.dispatchEvent(new Event("change", { bubbles: true }))
    }
  }

// Hands values to the multiselect Stimulus controller that owns the field
// (grapes, hops), which updates its badges and hidden inputs.
setMultiselectValues(name, values) {
  if (!values || values.length === 0) return

  const element = this.element.querySelector(`[data-controller~="multiselect"][data-multiselect-name="${name}"]`)
  const multiselect = element && this.application.getControllerForElementAndIdentifier(element, "multiselect")
  if (multiselect) multiselect.setValues(values)
}

  startMessageRotation() {
    this.messageIndex = 0
    this.updateSpinnerMessage()

    this.messageInterval = setInterval(() => {
      this.messageIndex = (this.messageIndex + 1) % this.spinnerMessages.length
      this.updateSpinnerMessage()
    }, 2500) // Change message every 2.5 seconds
  }

  stopMessageRotation() {
    if (this.messageInterval) {
      clearInterval(this.messageInterval)
      this.messageInterval = null
    }
  }

  updateSpinnerMessage() {
    if (this.hasSpinnerTextTarget) {
      this.spinnerTextTarget.textContent = this.spinnerMessages[this.messageIndex]
    }
  }

  showSuccessMessage() {
    if (this.hasSpinnerTextTarget) {
      this.spinnerTextTarget.textContent = this.successMessageValue
      this.spinnerTextTarget.classList.add("text-green-600", "font-semibold")
    }
  }

  showError(message) {
    // Create or update error message element
    let errorDiv = this.element.querySelector(".ai-error-message")
    if (!errorDiv) {
      errorDiv = document.createElement("div")
      errorDiv.className = "ai-error-message rounded-lg bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 p-4 mb-4"
      // Insert at top of form
      this.element.insertBefore(errorDiv, this.element.firstChild)
    }

    errorDiv.innerHTML = `
      <p class="text-sm text-red-800 dark:text-red-200">
        <strong>${this.errorPrefixValue}</strong> ${message}
      </p>
    `
  }
}
